"use client";
import { createContext, useCallback, useContext, useState } from "react";
import {
  IMAGES,
  WEB_COLORS,
} from "@/lib/constants";
import { setModalAttributes, modalState } from "@/lib/redux/slice/ModalSlice";
import { Stack, Typography } from "@mui/material";
import Image from "next/image";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "../Button";
import ModalComponent from "../Modal";
import FaceMismatchModal from "./FaceMismatchModal";
import NameMismatchModal from "./NameMismatchModal";
import SessionExpiredError from "./SessionExpiredError";
import AttemptError from "./AttemptError";
import DataProtectionModal from "./DataProtectionModal";
import TemporaryServerError from "./TemporaryServerError";
import ModalAlert from "./ModalAlert";
import { ERROR_TYPES } from "@/lib/utils/errorhandling.utils";
import LoaderComponent from "../Loader";
import { useEffect } from "react";

const ModalContext = createContext(null);

/**
 * 
 * Provides a context for managing modal button actions dynamically.
 * This is necessary because Redux states cannot accept non-serializable values like functions.
 */
 const ModalProvider = ({ children }) => {
  const [onButtonClick, setOnButtonClick] = useState(null);

  const setModalButtonAction = useCallback((callback) => {
    setOnButtonClick(() => callback);
  }, []);

  const clearModalButtonAction = useCallback(() => {
    setOnButtonClick(null);
  }, []);

  return (
    <ModalContext.Provider
      value={{
        onButtonClick,
        setModalButtonAction,
        clearModalButtonAction,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

const useModalContext = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error(
      "useModalContext must be used inside ModalProvider"
    );
  }

  return context;
};

/**
 * Custom hook returning a function to dispatch modal attributes. 
 * if no argument is provided, it will reset the modal to its initial state.
 */
export const useDispatchModalAttributes = () => {
  const dispatch = useDispatch();
  const { setModalButtonAction, clearModalButtonAction } = useModalContext();

  const dispatchModalAttributes = useCallback(
    (args) => {
      if (args === undefined) {
        dispatch(setModalAttributes(modalState));
        clearModalButtonAction();
      } else {
        const { onButtonClick, ...rest } = args;
        if (onButtonClick) {
          setModalButtonAction(onButtonClick);
        } else {
          clearModalButtonAction();
        }
        dispatch(setModalAttributes(rest));
      }
    },
    [dispatch, setModalButtonAction, clearModalButtonAction],
  );
  return dispatchModalAttributes;
};

const RejectModal = () => {
  const { open, message, type, title, image } = useSelector(
    (state) => state?.modal,
  );
  const dispatch = useDispatch();
  const params = useParams();
  const product = params?.product || "";
  const path = usePathname();
  const router = useRouter();

  const insideProductFlow = product && path && path?.includes(product);
  const handleButtonClick = () => {
    if (insideProductFlow) {
      // window.location.href = "/";
      dispatch(setModalAttributes({ isRouting: true, targetRoute: "/" }));
      router.push("/");
    }
    dispatch(setModalAttributes({ open: false, message: "", type: "" }));
  };

  const modalTitle = title
    ? title
    : type?.toLowerCase() === "closed"
      ? "Application Rejected"
      : "Application Under Review";

  return (
    <ModalComponent open={open} closeButtonRequired={false}>
      <Stack
        spacing={3}
        padding={2}
        alignItems="center"
        justifyContent={"center"}
      >
        <Image src={image || IMAGES.rejectedLoan} alt="Loan Rejected" />
        <Typography
          align="center"
          fontWeight={700}
          color={WEB_COLORS.primary}
          fontSize={{ xs: 20, md: 24 }}
        >
          {modalTitle}
        </Typography>
        <Typography
          align="center"
          fontWeight={400}
          color={WEB_COLORS.textFadeColor}
        >
          {type?.toLowerCase() === "closed"
            ? message
            : "Looks like, we would require to screen your application a little more. We will get back to you in next 24 hours"}
        </Typography>
        <Stack minWidth={"240px"}>
          <ButtonComponent onClick={handleButtonClick}>
            {insideProductFlow ? "Go to homepage" : "Ok"}
          </ButtonComponent>
        </Stack>
      </Stack>
    </ModalComponent>
  );
};

const RejectedLoanModal = ({}) => {
  const dispatch = useDispatch();
  const { open, message, type, title, image, isRouting, targetRoute, buttonLabel } = useSelector(
    (state) => state?.modal,
  );
  const { onButtonClick, clearModalButtonAction } = useModalContext();
  const handleClose = () => {
    dispatch(setModalAttributes({ open: false, message: "", type: "", isRouting: false, targetRoute: "" }));
    clearModalButtonAction();
  };

 const pathname = usePathname();
 const locale = useLocale();
 
  useEffect(() => {
    const normalizedPathname = pathname?.replace(`/${locale}`, "") || "/";
    const normalizedTarget = targetRoute?.replace(`/${locale}`, "") || "/";
    if (isRouting && normalizedPathname === normalizedTarget) {
      handleClose();
    }
  }, [isRouting, targetRoute, pathname]);

  const modalRender = (type) => {
    switch (type) {
      case ERROR_TYPES?.PAN_KYC_NAME_MATCH_CREDIT_QUEUE:
      case ERROR_TYPES?.NAME_MISMATCH_ERROR:
      case ERROR_TYPES?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE:
        return <NameMismatchModal />;

      case ERROR_TYPES?.FACE_MISMATCH_ERROR:
      case ERROR_TYPES?.DIGI_KYC_FAIL_FACEMATCH:
        return <FaceMismatchModal />;

      case ERROR_TYPES?.MANDATE_LIMIT_EXHAUSTED:
      case ERROR_TYPES?.CLOSED:
      case ERROR_TYPES?.CREDIT_QUEUE:
        return <RejectModal />;

      case ERROR_TYPES?.SESSION_EXPIRED:
        return <SessionExpiredError />;

      case ERROR_TYPES?.ATTEMPT_ERROR:
      case ERROR_TYPES?.INVALID_ACCOUNT_DETAILS:
        return <AttemptError />;

      case ERROR_TYPES?.ONE_MONEY_CONSENT:
        return <DataProtectionModal />;

      case ERROR_TYPES?.TEMPORARY_SERVER_ERROR:
        return <TemporaryServerError />;
        
      default:
        return (
          <ModalAlert
            description={message}
            open={open}
            title={title}
            closeButtonRequired={false}
            onPressClose={handleClose}
            onButtonClick={onButtonClick || handleClose}
            image={image || IMAGES.rejectedLoan}
            buttonLabel={buttonLabel || "Ok"}
          />
        );
    }
  };
  return (
    <>
      <LoaderComponent open={isRouting} />
      {modalRender(type)}
    </>
  );
};

const CentralisedModalWrapper = ({ children }) => {
  return (
    <ModalProvider>
      <RejectedLoanModal />
      {children}
    </ModalProvider>
  );
};

export default CentralisedModalWrapper;
